import { useState } from "react";
import { rankList, type RankStatus } from "@/mocks/blackMaterial";

const tabs: { key: RankStatus | "ALL"; label: string }[] = [
  { key: "ALL", label: "综合榜" },
  { key: "塌房", label: "塌房榜" },
  { key: "翻车", label: "翻车榜" },
  { key: "争议", label: "争议榜" },
];

const statusPill: Record<RankStatus, string> = {
  塌房: "bg-primary-100 text-primary-700",
  翻车: "bg-accent-100 text-accent-800",
  争议: "bg-secondary-200/70 text-foreground-600",
};

const deltaIcon: Record<RankItem["delta"], string> = {
  up: "ri-arrow-up-line text-primary-500",
  new: "ri-sparkling-2-line text-accent-500",
  down: "ri-arrow-down-line text-foreground-300",
  flat: "ri-subtract-line text-foreground-300",
};

interface RankItem {
  name: string;
  field: string;
  status: RankStatus;
  reason: string;
  heat: number;
  comments: string;
  delta: "up" | "down" | "new" | "flat";
}

export default function RankSection() {
  const [tab, setTab] = useState<RankStatus | "ALL">("ALL");
  const list = tab === "ALL" ? rankList : rankList.filter((r) => r.status === tab);

  return (
    <section id="ranking" className="bg-background-100">
      <div className="mx-auto max-w-[1200px] px-4 py-16 md:px-6 md:py-24">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col gap-3">
            <span className="text-xs font-semibold tracking-[0.18em] text-primary-500">争议主角 · 翻车名单</span>
            <h2 className="text-2xl font-bold text-foreground-950 md:text-4xl">本周翻车榜单</h2>
            <p className="max-w-xl text-sm leading-7 text-foreground-500">
              谁是当下最被追问的塌房与争议主角？榜单按复核后的讨论热度滚动重排，不代表定性结论。
            </p>
          </div>
          <div className="flex items-center gap-1 self-start rounded-full bg-background-50 p-1 md:self-auto">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
                  tab === t.key ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-3 lg:grid-cols-2">
          {list.map((r, i) => {
            const rank = tab === "ALL" ? rankList.indexOf(r) + 1 : i + 1;
            return (
              <div
                key={r.name}
                className="flex cursor-pointer items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-background-300"
              >
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md font-[number] text-lg ${
                    rank <= 3 ? "bg-accent-500 text-background-50" : "bg-secondary-200/70 text-foreground-700"
                  }`}
                >
                  {String(rank).padStart(2, "0")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-semibold text-foreground-950">{r.name}</span>
                    <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] ${statusPill[r.status]}`}>
                      {r.status}
                    </span>
                    <span className="hidden text-xs text-foreground-400 sm:inline">{r.field}</span>
                  </div>
                  <p className="mt-1 truncate text-xs text-foreground-500">{r.reason}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <div className="flex items-center gap-1 text-sm text-foreground-800">
                    <i className={`${deltaIcon[r.delta]} text-sm`} />
                    {r.heat}
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-foreground-400">
                    <i className="ri-chat-3-line" />
                    {r.comments}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}